import { deepClone } from '../utils';
import { logSessionDebug } from '../ui/session-debug';
import type { AssistantSegment, ChatMessage } from '../ui/chat/types';

interface HistoryWindowPayload {
  sessionId?: string;
  historyPending?: boolean;
  historyTotal?: number;
  historyBuffered?: number;
  historyRemaining?: number;
}

type MergeAssistantMessageFields = (target: ChatMessage, incoming: ChatMessage) => void;

export function stringifyAssistantSegments(message: ChatMessage | null | undefined): string {
  if (Array.isArray(message?.segments) && message.segments.length > 0) {
    return message.segments
      .map((segment: AssistantSegment) => {
        if (segment?.type === 'text') return segment.text || '';
        if (segment?.type === 'tool_call') return JSON.stringify({
          id: segment.id || '',
          name: segment.name || '',
          input: segment.input ?? null,
          result: segment.result ?? null,
        });
        return JSON.stringify(segment || null);
      })
      .join('\n');
  }
  return String(message?.content || '');
}

function summarizeMessage(message: ChatMessage | null): Record<string, unknown> | null {
  if (!message) return null;
  const text = stringifyAssistantSegments(message);
  return {
    role: message.role || null,
    contentLength: String(message.content || '').length,
    segmentCount: Array.isArray(message.segments) ? message.segments.length : 0,
    toolCallCount: Array.isArray(message.toolCalls) ? message.toolCalls.length : 0,
    textHash: `${text.length}:${text.slice(0, 24)}:${text.slice(-24)}`,
    timestamp: message.timestamp || null,
    firstResponseAt: message.firstResponseAt || null,
    lastAppendedAt: message.lastAppendedAt || null,
  };
}

export function summarizeHistoryCriticalMessages(messages: ChatMessage[]): Record<string, unknown> {
  const normalized = Array.isArray(messages) ? messages : [];
  const assistantMessages = normalized.filter((message) => message?.role === 'assistant');
  const userMessages = normalized.filter((message) => message?.role === 'user');
  const lastMessage = normalized.length > 0 ? normalized[normalized.length - 1] : null;
  const lastAssistant = assistantMessages.length > 0 ? assistantMessages[assistantMessages.length - 1] : null;
  return {
    messageCount: normalized.length,
    assistantCount: assistantMessages.length,
    userCount: userMessages.length,
    lastMessage: summarizeMessage(lastMessage),
    lastAssistant: summarizeMessage(lastAssistant),
  };
}

export function assistantMessagesLookSame(a: ChatMessage | null | undefined, b: ChatMessage | null | undefined): boolean {
  if (!a || !b || a.role !== 'assistant' || b.role !== 'assistant') return false;
  const aText = stringifyAssistantSegments(a).trim();
  const bText = stringifyAssistantSegments(b).trim();
  if (!aText || !bText) return false;
  return aText === bText || aText.startsWith(bText) || bText.startsWith(aText);
}

function messagesHaveSameIdentity(a: ChatMessage | null | undefined, b: ChatMessage | null | undefined): boolean {
  if (!a || !b || a.role !== b.role) return false;
  if (a.clientMessageId && b.clientMessageId) return a.clientMessageId === b.clientMessageId;
  if (a.timestamp && b.timestamp && a.timestamp === b.timestamp) return true;
  if (a.role === 'assistant') return assistantMessagesLookSame(a, b);
  return String(a.content || '') === String(b.content || '');
}

function mergeMessageFields(
  target: ChatMessage | null | undefined,
  incoming: ChatMessage | null | undefined,
  mergeAssistantMessageFields: MergeAssistantMessageFields,
): ChatMessage {
  if (!target || typeof target !== 'object') return deepClone(incoming);
  if (!incoming || typeof incoming !== 'object') return target;
  if (target.role === 'assistant' && incoming.role === 'assistant') {
    mergeAssistantMessageFields(target, incoming);
    return target;
  }
  return {
    ...target,
    role: incoming.role ?? target.role,
    content: incoming.content ?? target.content,
    attachments: Array.isArray(incoming.attachments) ? deepClone(incoming.attachments) : target.attachments,
    timestamp: incoming.timestamp || target.timestamp,
    clientMessageId: incoming.clientMessageId || target.clientMessageId,
  };
}

function findHistoryWindowOverlap(cachedMessages: ChatMessage[], incomingMessages: ChatMessage[]): number {
  const max = Math.min(cachedMessages.length, incomingMessages.length);
  // Server windows are small recent-message slices, so this backtracking match stays cheap and simple.
  for (let size = max; size > 0; size -= 1) {
    let matched = true;
    for (let offset = 0; offset < size; offset += 1) {
      if (!messagesHaveSameIdentity(cachedMessages[cachedMessages.length - size + offset], incomingMessages[offset])) {
        matched = false;
        break;
      }
    }
    if (matched) return size;
  }
  return 0;
}

function historyWindowLogBase(
  payload: HistoryWindowPayload,
  incomingMessages: ChatMessage[],
  cachedMessages: ChatMessage[],
): Record<string, unknown> {
  return {
    sessionId: payload.sessionId,
    payloadMessageCount: incomingMessages.length,
    cachedMessageCount: cachedMessages.length,
    historyTotal: Number.isFinite(payload.historyTotal) ? payload.historyTotal : null,
    historyBuffered: Number.isFinite(payload.historyBuffered) ? payload.historyBuffered : null,
    historyRemaining: Number.isFinite(payload.historyRemaining) ? payload.historyRemaining : null,
    payloadSummary: summarizeHistoryCriticalMessages(incomingMessages),
    cachedSummary: summarizeHistoryCriticalMessages(cachedMessages),
  };
}

export function mergeCachedHistoryWindow(
  payload: HistoryWindowPayload,
  messages: ChatMessage[],
  cachedMessages: ChatMessage[],
  mergeAssistantMessageFields: MergeAssistantMessageFields,
): ChatMessage[] {
  if (!payload?.sessionId || !payload?.historyPending) return messages;
  const incomingMessages = Array.isArray(messages) ? messages : [];
  const normalizedCachedMessages = Array.isArray(cachedMessages) ? cachedMessages : [];
  if (normalizedCachedMessages.length < incomingMessages.length) {
    logSessionDebug('reply-history:normalize-window', {
      ...historyWindowLogBase(payload, incomingMessages, normalizedCachedMessages),
      action: 'use-payload-window',
      reason: 'cache-not-longer',
    });
    return incomingMessages;
  }

  const overlap = findHistoryWindowOverlap(normalizedCachedMessages, incomingMessages);
  if (!overlap) {
    logSessionDebug('reply-history:normalize-window', {
      ...historyWindowLogBase(payload, incomingMessages, normalizedCachedMessages),
      action: 'no-overlap',
    });
    return incomingMessages;
  }

  const prefix = normalizedCachedMessages.slice(0, normalizedCachedMessages.length - overlap);
  const mergedOverlap = incomingMessages.slice(0, overlap).map((incoming, index) => (
    mergeMessageFields(normalizedCachedMessages[normalizedCachedMessages.length - overlap + index], incoming, mergeAssistantMessageFields)
  ));
  const appended = incomingMessages.slice(overlap);
  const merged = prefix.concat(mergedOverlap, appended);
  logSessionDebug('reply-history:normalize-window', {
    ...historyWindowLogBase(payload, incomingMessages, normalizedCachedMessages),
    action: 'merged-overlap',
    overlap,
    prefixCount: prefix.length,
    appendedCount: appended.length,
    resultSummary: summarizeHistoryCriticalMessages(merged),
  });
  return merged;
}
